import { bnPageHead } from "@/lib/bn-head";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { JsonLd } from "@/components/JsonLd";
import { SITE } from "@/content/site";
import { ArrowUpRight } from "lucide-react";

const URL = `${SITE.url}/bn/services`;
const TITLE = "সেবাসমূহ — এসইও, লোকাল এসইও ও AEO কনসালটিং (বাংলা)";
const DESC =
  "টেকনিক্যাল এসইও অডিট, লোকাল এসইও, ই-কমার্স এসইও, কনটেন্ট কৌশল এবং AEO/GEO — বাংলাদেশি ব্যবসার জন্য কী কী সেবা দিই, কীভাবে কাজ হয় এবং খরচ কত।";

const SERVICES = [
  {
    t: "টেকনিক্যাল এসইও অডিট",
    d: "ক্রল, ইনডেক্স, সাইট স্ট্রাকচার ও Core Web Vitals — যে ভিত্তিটা ঠিক না হলে বাকি কিছুই কাজ করে না। সাধারণত ৪০–১২০টি সমস্যা পাওয়া যায়, প্রভাব অনুযায়ী সাজানো।",
    p: "২০,০০০–৫০,০০০ টাকা (এককালীন)",
  },
  {
    t: "লোকাল এসইও",
    d: "Google Business Profile অপটিমাইজেশন, NAP ধারাবাহিকতা, রিভিউ সিস্টেম এবং ঢাকা, চট্টগ্রাম, সিলেট, খুলনা ও রংপুরের জন্য এলাকাভিত্তিক ল্যান্ডিং পেজ।",
    p: "মাসে ১৫,০০০–৩৫,০০০ টাকা",
  },
  {
    t: "ই-কমার্স এসইও",
    d: "ক্যাটাগরি আর্কিটেকচার, ফিল্টার/ফেসেট নিয়ন্ত্রণ, প্রোডাক্ট স্কিমা এবং ইন্টারনাল লিংকিং — যাতে অথরিটি বিক্রির পেজে পৌঁছায়।",
    p: "মাসে ৩৫,০০০–৮০,০০০ টাকা",
  },
  {
    t: "কনটেন্ট কৌশল",
    d: "কীওয়ার্ড-টু-পেজ ম্যাপ, টপিক্যাল হাব এবং লেখকের জন্য ব্যবহারযোগ্য ব্রিফ — বাংলা ও ইংরেজি দুই ভাষাতেই।",
    p: "মাসে ২৫,০০০–৬০,০০০ টাকা",
  },
  {
    t: "AEO ও GEO",
    d: "ChatGPT, Perplexity, Claude, Gemini এবং Google AI Overviews-এ উদ্ধৃত হওয়ার কাজ — স্কিমা, প্রশ্ন-ভিত্তিক কনটেন্ট ও এন্টিটি সিগন্যাল।",
    p: "মাসে ৩০,০০০–৭০,০০০ টাকা",
  },
  {
    t: "কনসালটেশন",
    d: "আপনার টিম কাজ করবে, আমি কৌশল ও রিভিউ দেব — মাসিক কল, অগ্রাধিকার তালিকা এবং কাজের যাচাই।",
    p: "ঘণ্টায় ২,০০০–৬,০০০ টাকা",
  },
];

const STEPS = [
  { t: "১. ডিসকভারি কল", d: "২০ মিনিট। ব্যবসার লক্ষ্য, বর্তমান অবস্থা ও বাজেট নিয়ে খোলা আলোচনা।" },
  { t: "২. অডিট ও রোডম্যাপ", d: "১০–১৪ দিন। টেকনিক্যাল অডিট, কীওয়ার্ড ম্যাপ, প্রতিযোগী বিশ্লেষণ ও ৯০ দিনের পরিকল্পনা।" },
  { t: "৩. বাস্তবায়ন", d: "মাসিক স্প্রিন্ট — ঠিক করা, প্রকাশ করা, লিংক অর্জন করা। প্রতিটি কাজের মালিকানা লিখিত।" },
  { t: "৪. রিপোর্টিং", d: "মাসিক রিপোর্ট Search Console ও GA4-র বাস্তব ডেটা থেকে, একটি মূল KPI-র বিপরীতে।" },
];

export const Route = createFileRoute("/bn/services/")({
  head: () =>
    bnPageHead({
      path: "/bn/services",
      enPath: "/services",
      title: TITLE,
      desc: DESC,
      keywords: ["seo service bangladesh", "এসইও সার্ভিস", "লোকাল এসইও সেবা"],
    }),
  component: BnServices,
});

function BnServices() {
  const schema = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${URL}/#service-bn`,
    name: "ফ্রিল্যান্সার তামাল — এসইও কনসালটিং",
    url: URL,
    inLanguage: "bn-BD",
    email: SITE.email,
    telephone: SITE.phone,
    areaServed: { "@type": "Country", name: "Bangladesh" },
    address: {
      "@type": "PostalAddress",
      streetAddress: SITE.streetAddress,
      addressLocality: SITE.city,
      addressRegion: SITE.region,
      postalCode: SITE.postalCode,
      addressCountry: SITE.countryCode,
    },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "এসইও সেবাসমূহ",
      itemListElement: SERVICES.map((s) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: s.t, description: s.d },
      })),
    },
  };

  return (
    <>
      <JsonLd data={schema} />
      <Section className="pt-8 pb-16">
        <div className="mb-8 text-xs uppercase tracking-wider text-muted-foreground">
          <Link to="/services" className="hover:text-foreground">EN</Link>
          <span className="mx-2">·</span>
          <span className="text-foreground">বাংলা</span>
        </div>
        <header className="max-w-3xl">
          <h1 className="font-display text-4xl sm:text-5xl leading-tight">
            সেবাসমূহ — <span className="text-gradient">এসইও, লোকাল এসইও ও AEO</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground">{DESC}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/bn/contact"
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-medium"
            >
              কাজ নিয়ে কথা বলুন <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/bn/blog"
              className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium"
            >
              আগে গাইডগুলো পড়ুন
            </Link>
            <Link to="/bn/services/seo" className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium">এসইও</Link>
            <Link to="/bn/services/aeo" className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium">AEO</Link>
            <Link to="/bn/services/geo" className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-medium">GEO</Link>
          </div>
        </header>
      </Section>

      <Section className="pb-16">
        <h2 className="font-display text-2xl mb-6">কী কী কাজ করি</h2>
        <div className="grid gap-6 sm:grid-cols-2">
          {SERVICES.map((s) => (
            <div key={s.t} className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-display text-xl">{s.t}</h3>
              <p className="mt-3 text-sm text-muted-foreground">{s.d}</p>
              <p className="mt-4 text-sm font-medium text-primary">{s.p}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm text-muted-foreground">
          দাম প্রকল্পের পরিধি অনুযায়ী বদলায়। প্রতিটি প্রস্তাবে কাজের স্কোপ, ডেলিভারেবল ও সময়সীমা লিখিত থাকে —
          কোনো লুকানো ফি নেই।
        </p>
      </Section>

      <Section className="pb-24">
        <h2 className="font-display text-2xl mb-6">কীভাবে কাজ হয়</h2>
        <ol className="grid gap-5 sm:grid-cols-2">
          {STEPS.map((s) => (
            <li key={s.t} className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-display text-lg">{s.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
            </li>
          ))}
        </ol>
      </Section>
    </>
  );
}
